import { useStore } from '../context/StoreContext';
import { Mail, MapPin, Phone, ArrowLeft } from 'lucide-react';

interface PolicyPageProps {
  page: string;
  onNavigate: (path: string) => void;
}

export function PolicyPage({ page, onNavigate }: PolicyPageProps) {
  const { settings } = useStore();

  const getPageData = () => {
    switch (page) {
      case 'sobre':
        return {
          title: 'Sobre a Origem do Aura Ateliê',
          subtitle: 'Nossa história de ofício, materialidade e compromisso com o tempo.',
          content: (
            <div className="space-y-4 text-xs sm:text-sm text-ink-muted leading-relaxed">
              <p>
                O <strong>Aura Ateliê</strong> nasceu da convicção de que os objetos que habitam nosso espaço diário não devem ser efêmeros. Em um mundo marcado pela aceleração e descartabilidade, escolhemos a lentidão do barro, o rigor do torno e a respiração das fibras naturais.
              </p>
              <p>
                Nossa produção de cerâmica ocorre inteiramente em São Paulo, utilizando argila grés brasileira e esmaltes minerais formulados no próprio estúdio a partir de cinzas vegetais e óxidos minerais. As queimas são realizadas a 1240°C em atmosfera oxidante, garantindo resistência mecânica e vitrificação completa.
              </p>
              <p>
                Além das peças físicas, expandimos nossa prática para o ambiente digital e a consultoria sensorial de interiores, permitindo que arquitetos, designers e apreciadores construam espaços de acolhimento e identidade genuína.
              </p>
            </div>
          ),
        };

      case 'envio':
        return {
          title: 'Prazos de Envio & Entrega',
          subtitle: 'Como cuidamos para que sua encomenda chegue com segurança.',
          content: (
            <div className="space-y-4 text-xs sm:text-sm text-ink-muted leading-relaxed">
              <h3 className="font-serif text-base font-semibold text-ink">1. Embalagem Protetora de Cerâmica</h3>
              <p>
                Sabemos da delicadeza das peças minerais. Por isso, desenvolvemos um protocolo rigoroso de embalagem em caixas duplas reforçadas, envoltas em mantas biodegradáveis e calços de papelão estruturado, sem plástico descartável.
              </p>

              <h3 className="font-serif text-base font-semibold text-ink">2. Prazos de Postagem</h3>
              <p>
                Peças em pronta entrega são despachadas em até <strong>2 dias úteis</strong> após a confirmação do pagamento. O prazo de transporte varia conforme o CEP de destino (em média de 3 a 7 dias úteis para capitais e principais regiões metropolitanas).
              </p>

              <h3 className="font-serif text-base font-semibold text-ink">3. Frete Cortesia</h3>
              <p>
                Oferecemos <strong>Frete Cortesia</strong> automático para todo o Brasil em compras a partir de R$ 450,00.
              </p>
            </div>
          ),
        };

      case 'trocas':
        return {
          title: 'Trocas, Devoluções & Avarias',
          subtitle: 'Garantia total de chegada intacta e respeito aos seus direitos.',
          content: (
            <div className="space-y-4 text-xs sm:text-sm text-ink-muted leading-relaxed">
              <h3 className="font-serif text-base font-semibold text-ink">1. Garantia de Chegada Perfeita</h3>
              <p>
                Se a sua peça sofrer qualquer avaria durante o transporte dos Correios ou transportadora, basta nos enviar uma fotografia da caixa e da peça danificada em até 7 dias após o recebimento. Nós enviaremos uma nova peça imediatamente sem qualquer custo de frete.
              </p>

              <h3 className="font-serif text-base font-semibold text-ink">2. Direito de Arrependimento (CDC)</h3>
              <p>
                Conforme o Código de Defesa do Consumidor brasileiro, você pode solicitar a devolução ou troca de produtos físicos no prazo de até 7 dias corridos após o recebimento. A peça deve estar sem uso e na embalagem original.
              </p>

              <h3 className="font-serif text-base font-semibold text-ink">3. Produtos Digitais</h3>
              <p>
                Devido à natureza de download imediato dos arquivos digitais, reembolsos são concedidos caso haja incompatibilidade técnica comprovada ou falha de integridade do arquivo fornecido.
              </p>
            </div>
          ),
        };

      case 'privacidade':
        return {
          title: 'Privacidade & Proteção de Dados (LGPD)',
          subtitle: 'Como tratamos seus dados pessoais com criptografia em repouso.',
          content: (
            <div className="space-y-4 text-xs sm:text-sm text-ink-muted leading-relaxed">
              <p>
                O <strong>Aura Ateliê</strong> leva a segurança de dados ao nível de excelência técnica. Seus dados pessoais sensíveis (CPF, nome completo, telefone e endereço de entrega) são criptografados no servidor com <strong>AES-256-GCM</strong> com vetores de inicialização únicos antes de serem gravados em nosso banco de dados relacional Cloudflare D1.
              </p>
              <h3 className="font-serif text-base font-semibold text-ink">Direitos do Titular</h3>
              <p>
                Em estrito cumprimento à Lei Geral de Proteção de Dados (Lei 13.709/2018 - LGPD), você possui o direito de solicitar a qualquer momento a confirmação de tratamento, acesso, correção ou exclusão definitiva dos seus dados através do nosso canal de atendimento ao titular.
              </p>
              <h3 className="font-serif text-base font-semibold text-ink">Sigilo Absoluto</h3>
              <p>
                Nunca comercializamos ou compartilhamos dados pessoais com redes de publicidade de terceiros. As informações de faturamento e entrega são utilizadas unicamente para o cumprimento do contrato de compra e emissão de documento fiscal.
              </p>
            </div>
          ),
        };

      case 'termos':
        return {
          title: 'Termos de Serviço & Licença',
          subtitle: 'Condições gerais de compra, uso da plataforma e acervo digital.',
          content: (
            <div className="space-y-4 text-xs sm:text-sm text-ink-muted leading-relaxed">
              <p>
                Ao navegar ou adquirir qualquer item no Aura Ateliê, você concorda com os presentes Termos de Serviço regidos pela legislação da República Federativa do Brasil.
              </p>
              <h3 className="font-serif text-base font-semibold text-ink">Licença do Acervo Digital</h3>
              <p>
                A aquisição de presets, pôsteres digitais ou arquivos 3D concede uma licença não exclusiva, intransferível e perpétua para uso em trabalhos pessoais e profissionais. É terminantemente proibida a revenda, compartilhamento coletivo ou sublicenciamento dos arquivos em plataformas de distribuição.
              </p>
            </div>
          ),
        };

      case 'contato':
      default:
        return {
          title: 'Atendimento & Concierge',
          subtitle: 'Estamos à disposição para encomendas, dúvidas de produtos e projetos sob medida.',
          content: (
            <div className="space-y-6 text-xs sm:text-sm text-ink-muted leading-relaxed">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 pt-2">
                <div className="bg-surface p-4 rounded-xs border border-border-subtle space-y-2">
                  <div className="flex items-center gap-2 text-ink font-semibold">
                    <Mail className="w-4 h-4 text-primary" /> E-mail Oficial
                  </div>
                  <p>{settings.contact_email}</p>
                  <p className="text-[11px] text-ink-muted">Resposta em até 24h úteis</p>
                </div>

                <div className="bg-surface p-4 rounded-xs border border-border-subtle space-y-2">
                  <div className="flex items-center gap-2 text-ink font-semibold">
                    <Phone className="w-4 h-4 text-primary" /> WhatsApp / Concierge
                  </div>
                  <p>{settings.contact_phone}</p>
                  <p className="text-[11px] text-ink-muted">Segunda a Sexta, das 10h às 18h</p>
                </div>
              </div>

              <div className="bg-surface p-4 rounded-xs border border-border-subtle space-y-2">
                <div className="flex items-center gap-2 text-ink font-semibold">
                  <MapPin className="w-4 h-4 text-primary" /> Ateliê de Criação
                </div>
                <p>{settings.address_line}</p>
                <p className="text-[11px] text-ink-muted">Visitas presenciais sob agendamento prévio</p>
              </div>
            </div>
          ),
        };
    }
  };

  const data = getPageData();

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12 space-y-8">
      <button
        onClick={() => onNavigate('/')}
        className="inline-flex items-center gap-1.5 text-xs text-ink-muted hover:text-ink transition-colors"
      >
        <ArrowLeft className="w-3.5 h-3.5" /> Voltar ao Início
      </button>

      <div className="border-b border-border-subtle pb-6 space-y-2">
        <span className="text-[10px] uppercase tracking-[0.2em] font-semibold text-primary">
          Institucional & Informações
        </span>
        <h1 className="text-3xl sm:text-4xl font-serif font-normal text-ink">
          {data.title}
        </h1>
        <p className="text-xs sm:text-sm text-ink-muted">{data.subtitle}</p>
      </div>

      <div className="bg-surface border border-border-subtle p-8 rounded-xs shadow-subtle">
        {data.content}
      </div>
    </div>
  );
}
